import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { slateColors } from './ContentPages/AppLandingPage/styles';

import { TaggedJourney } from '../utils/mapJourneysData';
import { EarlierButton, LaterButton } from './Controls/EarlierLaterButtons';
import JourneyCard from './JourneyCard';
import FeedbackCard from './Elements/FeedbackCard';
import SortOptions, { SortOption } from './JourneyCard/SortOptions';
import { sortJourneys } from '../utils/sortJourneys';


// Styled component for the journey list container
const JourneyListContainer = styled.div`
  margin-bottom: 24px;
  padding: 0 16px;
  @media (min-width: 768px) {
    padding: 24px 64px;  // Increased horizontal padding for desktop
    max-width: 600px;
  }
`;

const JourneyListContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
`;

const FeedbackCardWrapper = styled.div`
  margin-top: 24px;
`;

const JourneyCardsContainer = styled.div`
  display: flex;
  flex-direction: column; /* Stack journey cards vertically */
  gap: 32px; /* Space between individual journey cards */
  @media (min-width: 768px) {
    display: flex;
  }
`;

const DayDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DayDividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${slateColors[200]};
`;

const DayDividerLabel = styled.span`
  font-size: 0.75rem;
  color: ${slateColors[500]};
  white-space: nowrap;
`;

export const JourneyList: React.FC<{
  journeys: TaggedJourney[];
  earlierThan: string | null;
  laterThan: string | null;
  loading: boolean; // Add loading prop to the component
  fetchJourneys: (type: 'earlier' | 'later') => void;
  originName: string;
  destinationName: string;
  date: string;
  tripType: string;
}> = ({ journeys, earlierThan, laterThan, loading, fetchJourneys, originName, destinationName, date, tripType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Get initial sort option from localStorage or default to 'arrives-first'
  const getInitialSortOption = (): SortOption => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('lastUsedSortOption');
      if (saved && ['arrives-first', 'most-reliable', 'cheapest', 'least-busy', 'fastest', 'departs-first'].includes(saved)) {
        return saved as SortOption;
      }
    }
    return 'arrives-first';
  };

  const [sortOption, setSortOption] = useState<SortOption>(getInitialSortOption);

  // Save sort option to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedSortOption', sortOption);
    }
  }, [sortOption]);

  const handleSortChange = (sort: SortOption) => {
    setSortOption(sort);
  };

  // Sort journeys based on selected option
  const sortedJourneys = useMemo(() => {
    return sortJourneys(journeys, sortOption);
  }, [journeys, sortOption]);

  const handleJourneyClick = (journeyId: string) => {
    navigate(`/journey/${journeyId}`, { state: { from: location.pathname } });
  };

  return (
    <JourneyListContainer> {/* Wrap the content with the new styled container */}
      {journeys.length > 0 && (
        <SortOptions
          journeys={journeys}
          selectedSort={sortOption}
          onSortChange={handleSortChange}
          loading={loading}
        />
      )}
      <JourneyListContent>
        {earlierThan && !loading && (
          <ButtonContainer>
            <EarlierButton onClick={() => fetchJourneys('earlier')} />
          </ButtonContainer>
        )}
        <JourneyCardsContainer>
          {sortedJourneys.length > 0 &&
            sortedJourneys.map((journey, index) => {
              const currDate = journey.legs?.[0]?.plannedDeparture ? new Date(journey.legs[0].plannedDeparture) : null;
              const prevDate = index > 0 && sortedJourneys[index - 1].legs?.[0]?.plannedDeparture
                ? new Date(sortedJourneys[index - 1].legs[0].plannedDeparture)
                : null;
              const showDayDivider = index > 0 && currDate && prevDate &&
                currDate.toDateString() !== prevDate.toDateString();

              return (
                <React.Fragment key={journey.id}>
                  {showDayDivider && currDate && (
                    <DayDivider>
                      <DayDividerLine />
                      <DayDividerLabel>
                        {currDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })}
                      </DayDividerLabel>
                      <DayDividerLine />
                    </DayDivider>
                  )}
                  <JourneyCard
                    onClick={() => handleJourneyClick(journey.id)}
                    journey={journey}
                    referenceDate={date}
                  />
                </React.Fragment>
              );
            })
          }
        </JourneyCardsContainer>
        {laterThan && !loading && (
          <ButtonContainer>
            <LaterButton onClick={() => fetchJourneys('later')} />
          </ButtonContainer>
        )}
      </JourneyListContent>
      {!loading && (
        <FeedbackCardWrapper>
          <FeedbackCard 
            source={`Journey options from ${originName} to ${destinationName}, ${tripType} on ${date}`}
            journeys={sortedJourneys}
            searchParams={{
              originName,
              destinationName,
              date,
              tripType
            }}
          />
        </FeedbackCardWrapper>
      )}
    </JourneyListContainer>
  );
};
